<template>
  <div :class="$style.dashboard">
    <div :class="$style.header">
      <h1 :class="$style.title">Business Profile</h1>
      <p :class="$style.description">
        {{ subheading }}
      </p>
      <p :class="$style.steps">
        {{ numCompletedSteps }}/{{ numberOfSteps }} Steps Complete
      </p>
    </div>
    <div :class="$style.body">
      <ProfileInfoBoxPanel />
      <div>
        <ProfileApplicationStep
          v-for="(step, index) in Object.values(businessProfileSteps)"
          :key="step.name"
          :title="step.label"
          :is-disabled="step.isDisabled"
          :state="getStepState(step)"
          :destination="getStepDestination(step)"
          :step-count="index + 1"
          :action-required="getActionRequired(step)"
        />
        <DSButton
          v-if="isBusinessProfileComplete"
          :class="$style.cta"
          label="Finish"
          @click="handleCtaClick()"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import analytics, { SEGMENT_EVENTS } from '@/utils/analytics';
import { RouteNames } from '@/data/routes';
import DSButton from 'primevue/button';
import ProfileApplicationStep from '@/pages/profile/components/ProfileApplicationStep.vue';
import ProfileInfoBoxPanel from '@/pages/profile/components/ProfileInfoBoxPanel.vue';

export default {
  components: {
    ProfileApplicationStep,
    DSButton,
    ProfileInfoBoxPanel,
  },
  async mounted() {
    await this.$store.dispatchApiAction('GET_FISCAL_DOCUMENT_CHECKS', {
      businessId: this.businessId,
    });

    analytics.track(SEGMENT_EVENTS.viewed_payments_profilepage);
  },
  computed: {
    ...mapGetters([
      'businessProfileSteps',
      'isPrimaryBankAccountDisconnected',
      'fiscalDocumentsUpToDate',
      'bankAccounts',
      'businessId',
      'isInFinancialDocsSegment',
      'isUploadBankStatementsStepComplete',
      'businessOnPlaidBypass',
      'debtObligationsRequired',
      'isBusinessProfileComplete',
      'fiscalStartDate',
      'financialDetailsDestination',
      'businessCapacityApproved',
    ]),
    numCompletedSteps() {
      return Object.keys(this.businessProfileSteps).filter(
        (step) => this.businessProfileSteps[step].isComplete,
      ).length;
    },
    numberOfSteps() {
      return Object.keys(this.businessProfileSteps).length;
    },
    subheading() {
      return this.numCompletedSteps < 4
        ? 'For access to funding, please complete your business profile.'
        : 'Your business profile is complete. Access or edit your information at anytime through the sections below.';
    },
  },
  methods: {
    getStepState(step) {
      if (
        step.name === RouteNames.CONNECTED_ACCOUNTS.INDEX &&
        this.userHasActiveBillsAdvanceAndDisconnectedSalesAccount
      ) {
        return 'warning';
      }

      if (step.name === RouteNames.BANKING.INDEX) {
        if (!this.bankAccounts.length) {
          return 'incomplete';
        } else if (
          (this.isPrimaryBankAccountDisconnected &&
            !this.businessOnPlaidBypass) ||
          (this.businessOnPlaidBypass &&
            !this.isUploadBankStatementsStepComplete)
        ) {
          return 'warning';
        }
      }

      return step.isComplete ? 'complete' : 'incomplete';
    },
    getActionRequired(step) {
      if (step.isDisabled) {
        return false;
      }

      if (step.name === RouteNames.BANKING.INDEX) {
        if (!this.bankAccounts.length) {
          return false;
        }

        if (
          this.isPrimaryBankAccountDisconnected &&
          !this.businessOnPlaidBypass
        ) {
          return true;
        }

        if (
          this.businessOnPlaidBypass &&
          !this.isUploadBankStatementsStepComplete
        )
          return true;
      }

      if (step.name === RouteNames.DEBT_OBLIGATIONS.INDEX) {
        if (this.businessCapacityApproved) {
          return false;
        }
        if (
          this.isInFinancialDocsSegment &&
          !this.fiscalDocumentsUpToDate &&
          !this.fiscalStartDate
        ) {
          return true;
        }
        if (this.debtObligationsRequired) {
          return true;
        }
      }

      return false;
    },
    getStepDestination(step) {
      if (step.name === RouteNames.DEBT_OBLIGATIONS.INDEX) {
        return this.financialDetailsDestination;
      }
      return step.name;
    },
    handleCtaClick() {
      this.$router.push({
        name: RouteNames.HOME.INITIAL,
      });
    },
  },
};
</script>

<style lang="less" module>
.dashboard {
  font-family: @regular-font;

  .header {
    text-align: left;

    @media only screen and (min-width: 960px) {
      text-align: center;
    }
  }

  .title {
    font-weight: 500;
    font-size: 20px;
    line-height: 1.4;
    margin: 1.125rem 0 1.5rem;
    color: #000000;

    @media only screen and (min-width: 960px) {
      margin: 1.875rem 0 1.5rem;
      font-size: 26px;
      line-height: 1.23;
    }
  }

  .description {
    font-weight: 300;
    font-size: 16px;
    line-height: 1.5;
    margin-bottom: 1.5rem;
    color: #393939;
  }

  .steps {
    font-weight: 700;
    font-size: 16px;
    line-height: 1.5;
    margin-bottom: 3rem;
    color: #393939;

    @media only screen and (min-width: 960px) {
      margin-bottom: 5rem;
    }
  }

  .body {
    display: grid;
    row-gap: 3rem;
    grid-template-columns: repeat(1, minmax(0, 1fr));

    .cta {
      width: 100%;
    }

    @media only screen and (min-width: 960px) {
      grid-template-columns: repeat(2, minmax(0, 1fr));
      column-gap: 1.5625rem;
    }
  }
}
</style>
